import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Container,
  FormGroup,
  FormLabel,
  Grid,
  Box,
  CircularProgress,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Select,
  MenuItem,
  InputBase,
  InputLabel,
  InputAdornment,
  useTheme,
} from '@mui/material';
import { makeStyles, withStyles } from '@mui/styles';
import { useRedirectStartupStatus, useStartupRoute } from 'hooks';
import CustomTextFieldOld from 'components/common/CustomTextFieldOld';
import {
  CustomButton,
  CustomTextField,
  CustomTextFieldAmountNoDecimal,
  CustomTextFieldWithoutControl,
  MetaTags,
} from 'components/common';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ApiGet, ApiPost } from 'helpers/Api/ApiData';
import { toast, ToastContainer } from 'react-toastify';

import CommonLayout from 'components/common/CommonLayout';

import AddIcon from '@mui/icons-material/Add';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { useRouter } from 'next/router';
import { useUser } from 'context/user.context';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { object } from 'yup';
import { User } from 'types';
import Head from 'next/head';

const BootstrapInput = withStyles((theme) => ({
  input: {
    borderRadius: 5,
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #ced4da',
    fontSize: 16,
    padding: '26px 26px 26px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:focus': {
      borderRadius: 5,
      backgroundColor: '#fff',
    },
  },
}))(InputBase);

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export default function StartupPitch() {
  const theme = useTheme();
  const useStyles = makeStyles({
    startUpPitchWrap: {
      minHeight: 'calc(100vh - 90px)',
    },
    heading: {
      maxWidth: 650,
      margin: '0 auto 20px',
      '& h5': {
        fontWeight: '500',
        fontSize: 20,
        marginBottom: 20,
        lineHeight: 1,
      },
      '& h4': {
        textAlign: 'center',
        fontWeight: '500',
        fontSize: 20,
        marginBottom: 20,
        lineHeight: 1,
      },
    },
    startUpForm: {
      '& .MuiFormGroup-root': {
        marginBottom: 15,
      },
      '& .MuiFormLabel-root': {
        // color: "#000",
        fontSize: 14,
        color: '#111111',
        lineHeight: 1.2,
        padding: 0,
        [theme.breakpoints.down('xs')]: {
          fontSize: 16,
        },
      },
      '& .MuiInputLabel-outlined': {
        transform: 'translate(15px, 15px) scale(1)',
      },
      '& .MuiInputLabel-shrink': {
        transform: 'translate(15px, -8px) scale(1)',
        backgroundColor: '#fff',
        fontSize: 12,
        padding: [[0, 11]],
        color: 'rgba(17, 17, 17, 0.5)',
      },
      '& .MuiInputBase-formControl': {
        '& .MuiOutlinedInput-input': {
          padding: [[15, 15]],
          fontSize: 14,
          [theme.breakpoints.down('xs')]: {
            fontSize: 16,
            padding: [[15, 15]],
          },
        },
        '& .Mui-disabled': {
          backgroundColor: '#e0fff8',
          color: '#4e5d5a',
        },
      },
      '& .MuiSelect-select': {
        padding: [[12, 15]],
        fontSize: 14,
        [theme.breakpoints.down('xs')]: {
          fontSize: 16,
          padding: [[15, 15]],
        },
      },
    },
    firstName: {
      [theme.breakpoints.down('xs')]: {
        marginBottom: theme.spacing(-2),
      },
    },
    spaceDivider: {
      height: 20,
    },
    fileUpload: {
      '& .MuiFormControl-root': {
        display: 'none',
      },
      '& [type=file]': {
        display: 'none',
      },
    },
    uploadBtn: {
      backgroundColor: '#b5aeae',
      color: '#fff',
      fontWeight: 'bold',
      padding: '3px 30px',
      cursor: 'pointer',
      display: 'inline-block',
      minWidth: 160,
      textAlign: 'center',
    },
    uploadSuccessBtn: {
      backgroundColor: '#008B5E',
      color: '#fff',
      fontWeight: 'bold',
      padding: '3px 30px',
      cursor: 'pointer',
      display: 'inline-block',
      minWidth: 160,
      textAlign: 'center',
    },
    requiredText: {
      fontSize: 12,
      marginLeft: 10,
    },
    formBtn: {
      width: 300,
      margin: '20px auto',
      '& button': {
        marginTop: 20,
        '& span': {
          fontSize: 18,
        },
      },
    },
    divider: {
      height: 45,
    },
    hr: {
      height: '0.5px',
      backgroundColor: '#b2efdc',
      border: 'none',
      margin: '30px 0',
    },
    error: {
      fontSize: 12,
      lineHeight: 1,
      display: 'flex',
      flexWrap: 'wrap',
      marginTop: 5,

      '& svg': {
        fontSize: 14,
        verticalAlign: 'middle',
        marginRight: 4,
      },
      '& .MuiInputBase-formControl': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#DD6161',
        },
      },
    },
    errorWrapper: {
      '& .MuiInputBase-formControl': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#DD6161',
        },
      },
    },
    datePikerInput: {
      borderRadius: 4,
      position: 'relative',
      backgroundColor: theme.palette.background.paper,
      border: '1px solid #8c8c8c',
      padding: '5px 15px',
      '& .MuiInput-underline:before': {
        content: 'normal',
      },
      '& .MuiInput-underline:after': {
        content: 'normal',
      },
      '& .MuiInputBase-input': {
        padding: 0,
        fontSize: 16,
        color: '#666666',
      },
      '& .MuiIconButton-root': {
        padding: 7,
        '& svg': {
          fontSize: '1.2rem',
        },
      },
    },
    addInvestor: {
      color: '#008B5E',
      cursor: 'pointer',
      // marginTop: 30,
      '& svg': {
        verticalAlign: 'middle',
        backgroundColor: '#008B5E',
        color: '#fff',
        borderRadius: 4,
        marginRight: 10,
      },
      '& span': {
        verticalAlign: 'middle',
      },
    },
    subtitle: {
      display: 'block',
      marginBottom: 15,
      fontSize: 16,
    },
    RadioGroup: {
      margin: '0px !important',
      alignItems: 'start',
    },
    safeLabel: {
      display: 'flex',
      flexWrap: 'wrap',
      flexDirection: 'column',
    },
    formControl: {
      marginTop: '-6px',
    },
    FundingField: {
      position: 'relative',
      '&:before': {
        content: "''",
        position: 'absolute',
        width: '1px',
        height: '47px',
        left: '53px',
        top: '1px',
        background: '#dfdfdf',
        zIndex: 1,
        [theme.breakpoints.down('xs')]: {
          height: '50px',
        },
      },
      '& .MuiFormLabel-root': {
        marginLeft: '50px',
        '&:focus': {
          marginLeft: 0,
        },
      },
      '& .MuiInputAdornment-root': {
        left: '0px !important',
      },
      '& .MuiTextField-root': {
        '& .MuiOutlinedInput-root': {
          // border: '1px solid',
          '& h5': {
            color: '#a3a8a6',
            fontSize: '30px',
            margin: [[0, 0, 0, 20]],
            fontWeight: 400,
            lineHeight: 1,
          },

          '& .MuiInputBase-input': {
            paddingLeft: '70px',
            border: 'none',
          },
        },
      },
      '& legend': {
        marginLeft: '50px',
      },
    },
  });
  useRedirectStartupStatus();
  const [, , setUser] = useUser();
  const userInfo = useStartupRoute();
  const userData = userInfo.user;
  // const userData = Auth.getUserData();
  // const [draft] = useState<any>(
  //   userData && userData?.company ? userData?.company : {},
  // );
  const [saveAsADraft, setSaveAsADraft] = useState(false);
  const draft: Partial<User['company']> = userData?.company || {};
  const classes = useStyles();
  const history = useRouter();
  const router = useRouter();

  interface FormFields extends Record<any, any> {
    company_name: string;
    product_name: string;
    linkedIn_url: string;
    stage: string; //seed, preseed
    website_url: string;
    industry: string;
    CIN_number: string;
    company_pan_number: string;
    type_of_funding: '1' | '2';
    funding_goal: string;
    funding_received_earlier: 'true' | 'false';
  }

  const schema = object().shape({
    company_name: yup
      .string()
      .test({
        name: 'company_name_is_required',
        message: 'Company name is required',
        test: (value) => !!value,
      })
      .matches(
        /^[.@&]?[a-zA-Z0-9]+[!.@&()]?[a-zA-Z0-9!()]+/,
        'Invalid Company Name',
      ),

    product_name: yup
      .string()
      .test({
        name: 'product_name_is_required',
        message: 'Product name is required',
        test: (value) => !!value,
      })
      .matches(
        /^[.@&]?[a-zA-Z0-9]+[!.@&()]?[a-zA-Z0-9!()]+/,
        'Invalid Product Name',
      ),

    linkedIn_url: yup
      .string()
      .required('LinkedIn URL is required')
      // .test({
      //   name: 'linkedIn_url_is_required',
      //   message: 'URL is required',
      //   test: (value) => (saveAsADraft ? !!value : true),
      // })
      .matches(
        /^((https?:\/\/)?((www|\w\w)\.)?linkedin\.com\/)((([\w]{2,3})?)|([^\/]+\/(([\w|\d-&#?=])+\/?){1,}))$/,
        'Invalid linkedin url',
      ),

    stage: yup.string().required('Stage is required'),
    // test({
    //   name: 'stage_is_required',
    //   message: 'Stage is required',
    //   test: (value) => !!value,
    // }),

    website_url: yup
      .string()
      .matches(
        /^((https?|ftp|smtp):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/i,
        'Invalid Website',
      ),

    industry: yup.string().required('Industry is required'),
    // .test({
    //   name: 'industry_is_required',
    //   message: 'Industry is required',
    //   test: (value) => (saveAsADraft ? !!value : true),
    // }),
    CIN_number: yup
      .string()
      .required('CIN number is required')
      // .test({
      //   name: 'CIN_number_is_required',
      //   message: 'CIN number is required',
      //   test: (value) => (saveAsADraft ? !!value : true),
      // })
      .matches(
        /^([L|U]{1})([0-9]{5})([A-Za-z]{2})([0-9]{4})([A-Za-z]{3})([0-9]{6})$/,
        'Invalid CIN number',
      ),
    company_pan_number: yup
      .string()
      .required('Company PAN is required')
      // .test({
      //   name: 'company_pan_number_is_required',
      //   message: 'Company PAN number is required',
      //   test: (value) => !!value,
      // })
      .matches(
        /^([a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}?$/i,
        'Invalid PAN number',
      ),
    funding_goal: yup
      .string()
      .required('Funding goal is required')
      .test(
        'funding_goal_validation',
        'Minimum goal amount is 20 Lakh',
        (value) => {
          if (!value) {
            return false;
          }
          return parseInt(value?.toString()?.replaceAll(',', '')) >= 2000000;
        },
      ),
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    getValues,
  } = useForm<FormFields>({
    mode: 'all',
    // defaultValues: {
    //   // company_name: draft.company_name || 'default company name',
    // },
    resolver: yupResolver(schema),
  });
  // const watch = useWatch({ control });
  // console.log('watch', watch);
  // console.log('getValues', getValues());

  useEffect(() => {
    if (userData) {
      // setValue([
      //   { company_name: userData.company?.company_name },
      //   // { phone: userData.phone },
      // ]);
      setValue('company_name', userData.company?.company_name || '');
      setValue('product_name', userData.company?.product_name || '');
      setValue('linkedIn_url', userData.company?.linkedIn_url || '');
      setValue('stage', userData.company?.stage || '');
      setValue('website_url', userData.company?.website_url || '');
      setValue('CIN_number', userData.company?.CIN_number || '');
      setValue(
        'company_pan_number',
        userData.company?.company_pan_number || '',
      );
      setValue(
        'funding_goal',
        userData.company?.funding_goal?.toLocaleString('en-IN') || '',
      );
      setValue(
        'funding_received_earlier',
        userData.company?.funding_received_earlier ? 'true' : 'false',
      );
      if (userData?.company?.old_investors) {
        userData.company.old_investors.forEach((obj, index) => {
          setValue(
            `name${index}`,
            userData?.company?.old_investors?.[index].name,
          );
          setValue(
            `amount${index}`,
            userData?.company?.old_investors?.[index].amount,
          );
        });
      }
    }
  }, [userData, setValue, userData?.company?.old_investors]);

  const [loader, setLoader] = useState(false);
  const [isEmptyInvestor, setIsEmptyInvestor] = useState<any[]>([]);
  const [invalidAmount, setInvalidAmount] = useState<any[]>([]);
  const [draftLoader, setDraftLoader] = useState(false);
  const [fundingType, setFundingType] = useState('1');
  const [fundingReceivedEarlier, setFundingReceivedEarlier] = useState<
    'true' | 'false'
  >(
    userData &&
      userData?.company &&
      userData?.company.funding_received_earlier === true
      ? 'true'
      : 'false',
  );

  useEffect(() => {
    setFundingReceivedEarlier(
      userData?.company?.funding_received_earlier === true ? 'true' : 'false',
    );
    if (!userData?.company?.type_of_funding) {
      return;
    }
    setFundingType(userData?.company?.type_of_funding?.toString());
  }, [userData?.company]);

  const [investors, setInvestors] = useState(
    userData && userData?.company && userData?.company.old_investors
      ? userData?.company.old_investors
      : [],
  );
  const [industriesData, setIndustriesData] = useState<any[] | undefined>();

  useEffect(() => {
    // if (getPaths(history)) {
    fetchIndustries();
    // }
  }, []);

  // useEffect(() => {
  //   if (Object.keys(errors).length)
  //     toast.error(
  //       'Some details are missing. Please scroll up to enter all details.',
  //       { autoClose: 5000, hideProgressBar: true },
  //     );
  // }, [handleSubmit, errors]);

  useEffect(() => {
    !investors.length &&
      setInvestors([
        ...investors,
        {
          name: '',
          amount: '',
        },
      ]);
  }, [investors]);

  const addAnotherInvestor = () => {
    setInvestors([
      ...investors,
      {
        name: '',
        amount: '',
      },
    ]);
  };

  const fetchIndustries = async () => {
    ApiGet(`industries/fetch`)
      .then((res: any) => {
        if (res.data.statusCode === 200) {
          setIndustriesData(res.data.data);
        }
        toast.error(res.data.error, { autoClose: 5000, hideProgressBar: true });
      })
      .catch((_err: any) => {
        toast.error('Internal server error!', {
          autoClose: 5000,
          hideProgressBar: true,
        });
      });
  };

  // const investorOnChangeHandler = (
  //   event: any,
  //   index: number,
  //   onChange?: any,
  // ) => {
  //   const investorsCopy = investors.slice();
  //   investorsCopy[index] = {
  //     ...investorsCopy[index],
  //     [event.target.name]:
  //       event.target.name === 'amount'
  //         ? onChangeAllNumberValue(event, onChange, 'amount')
  //         : event.target.value,stage
  //   setInvalidAmount([]);
  // };
  const investorOnChangeHandler = (
    event: any,
    index: number,
    onChange?: any,
  ) => {
    const investorsCopy = investors.slice();
    investorsCopy[index] = {
      ...investorsCopy[index],
      [event.target.name]:
        event.target.name === 'amount'
          ? onChangeAllNumberValue(event, onChange, 'amount')
          : event.target.value,
    };
    setInvestors(investorsCopy);
    setIsEmptyInvestor([]);
    setInvalidAmount([]);
  };

  const deleteInvestor = (index: number) => {
    const investorsCopy = investors.slice();
    investorsCopy.splice(index, 1);
    setInvestors(investorsCopy);
  };

  const investorValidation = () => {
    if (fundingReceivedEarlier !== 'true') {
      return false;
    }
    investors.filter((item, i: number) => {
      if (item.name === '' || item.amount === '' || item.amount === '0') {
        isEmptyInvestor.push(i);
      }
      return true;
    });
    if (isEmptyInvestor.length || invalidAmount.length) {
      return true;
    }
  };
  console.log(investors);

  const onDraft = async (_data: any) => {
    if (!userData) return;
    setDraftLoader(true);
    const investorDataWithAmount = investors.map((item) => {
      return {
        ...item,
        amount:
          typeof item.amount !== 'number'
            ? parseInt(item?.amount?.toString()?.replaceAll(',', '') || '0')
            : item.amount,
      };
    });
    const formValues = getValues();
    const draftData = {
      ...formValues,
      type_of_funding: fundingType,
      funding_received_earlier: fundingReceivedEarlier,
      funding_goal: formValues.funding_goal
        ? typeof formValues.funding_goal !== 'number'
          ? formValues.funding_goal.replaceAll(',', '')
          : formValues.funding_goal
        : undefined,
      // id: draft && draft.id && draft.id,
      old_investors:
        fundingReceivedEarlier === 'true' ? investorDataWithAmount : [],
      status: 'draft',
    };

    // const draftData = {
    //   ...data,
    //   type_of_funding: fundingType,
    //   funding_received_earlier: fundingReceivedEarlier,
    //   funding_goal:
    //     typeof data.funding_goal !== 'number'
    //       ? data.funding_goal.replaceAll(',', '')
    //       : data.funding_goal,
    //   // id: draft && draft.id && draft.id,
    //   old_investors:
    //     fundingReceivedEarlier === 'true' ? investorDataWithAmount : [],
    //   status: 'draft',
    // };
    await ApiPost(`startup/add`, draftData)
      .then((res: any) => {
        if (res.data.statusCode === 200) {
          setDraftLoader(false);
          userData.company = res.data.data;
          setUser((s) => ({
            ...s,
            user: { ...userData },
          }));
          // Auth.setUserData(userData);
          toast.success('Your startup details have been saved.', {
            autoClose: 5000,
            hideProgressBar: true,
          });
        } else {
          toast.error(res.data.message, {
            autoClose: 5000,
            hideProgressBar: true,
          });
          setDraftLoader(false);
        }
      })
      .catch((err: any) => {
        toast.error(err && err.message, {
          autoClose: 5000,
          hideProgressBar: true,
        });
        setDraftLoader(false);
      });
  };

  const onSave = async (data: any) => {
    if (!userData) return;
    setLoader(true);
    if (investorValidation()) {
      toast.error(
        'Some details are missing. Please scroll up to enter all details.',
        { autoClose: 5000, hideProgressBar: true },
      );
      setLoader(false);
      return;
    }
    const investorDataWithAmount = investors.map((item) => {
      return {
        ...item,
        amount:
          typeof item.amount !== 'number'
            ? parseInt(item?.amount?.toString()?.replaceAll(',', '') || '0')
            : item.amount,
      };
    });
    const submitData = {
      ...data,
      type_of_funding: Number(fundingType),
      funding_received_earlier: fundingReceivedEarlier,
      funding_goal:
        typeof data.funding_goal !== 'number'
          ? data.funding_goal.replaceAll(',', '')
          : data.funding_goal,
      // id: draft && draft.id && draft.id,
      old_investors:
        fundingReceivedEarlier === 'true' ? investorDataWithAmount : [],
      status: 'submit',
    };
    await ApiPost(`startup/add`, submitData)
      .then((res: any) => {
        if (res.data.statusCode === 200) {
          userData.company = res.data.data;
          userData.status = 2;
          setUser((s) => ({
            ...s,
            user: { ...userData },
          }));
          // Auth.setUserData(userData);
          setLoader(false);
          history.push('/startup-agreement');
        } else {
          toast.error(res.data.message, {
            autoClose: 5000,
            hideProgressBar: true,
          });
          setLoader(false);
        }
      })
      .catch((err: any) => {
        toast.error(err && err.message, {
          autoClose: 5000,
          hideProgressBar: true,
        });
        setLoader(false);
      });
  };

  const onChangeAllNumberValue = (e: any, onChange: Function, amount: any) => {
    // const addCommas = num => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const removeNonNumeric = (num: number | string) =>
      num.toString().replace(/[^0-9\.]/g, '');
    // const formattedValue = addCommas(removeNonNumeric(e.target.value));
    const formattedValue = Number(
      removeNonNumeric(e.target.value),
    ).toLocaleString('en-IN');
    onChange(formattedValue);
    if (amount) {
      return formattedValue;
    }
  };

  const valueWithComma = (num: string | number = '') =>
    num && num?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const onSubmitWithDraft = async (handleSubmit: any) => {
    setSaveAsADraft(true);
    setDraftLoader(true);
    await sleep(2000);
    setDraftLoader(false);
    handleSubmit();
  };

  const onSubmitWithLaunch = async (handleSubmit: any) => {
    setSaveAsADraft(false);
    setLoader(true);
    await sleep(2000);
    setLoader(false);
    handleSubmit();
  };

  if (!userData) {
    return null;
  }
  if (!router.isReady) {
    return null;
  }
  return (
    userData && (
      <>
        <MetaTags
          title="Create Startup Profile - POD"
          description="Create Startup Profile by submitting startup details"
          imageUrl={`${process.env.NEXT_PUBLIC_FRONTEND_URL}pod-logo.png`}
          url={process.env.NEXT_PUBLIC_FRONTEND_URL!}
          keywords="Startup Fundraising"
        />
        <Head>
          <meta name="robots" content="noindex,nofollow" />
        </Head>
        {/* <CommonDescription metaName="createStartupProfile" /> */}
        <CommonLayout>
          <ToastContainer />
          <div className={classes.startUpPitchWrap}>
            <Container>
              <Grid container justifyContent="center">
                <Grid item md={8}>
                  <div className={classes.heading}>
                    <Typography variant="h4" component="h4">
                      Please enter your company details
                    </Typography>
                    <Typography variant="h5" component="h5">
                      Your details
                    </Typography>
                  </div>
                  <div className={classes.startUpForm}>
                    <form
                    // onBlur={async () => {
                    //     await sleep(0)
                    //     if (Object.keys(errors).length) toast.error('Some details are missing. Please scroll up to enter all details.', { autoClose: 3000, hideProgressBar: true })
                    // }}
                    >
                      <FormGroup>
                        <Grid container alignItems="center" spacing={4}>
                          <Grid
                            item
                            md={6}
                            sm={6}
                            xs={12}
                            className={classes.firstName}
                          >
                            <CustomTextFieldWithoutControl
                              label="First Name"
                              autoComplete="off"
                              variant="outlined"
                              defaultValue={userData.first_name}
                              disabled
                              fullWidth
                            />
                          </Grid>
                          <Grid item md={6} sm={6} xs={12}>
                            <CustomTextFieldWithoutControl
                              label="Last Name"
                              autoComplete="off"
                              variant="outlined"
                              defaultValue={userData.last_name}
                              disabled
                              fullWidth
                            />
                          </Grid>
                        </Grid>
                      </FormGroup>
                      <FormGroup>
                        <CustomTextFieldWithoutControl
                          label="Mobile"
                          autoComplete="off"
                          variant="outlined"
                          defaultValue={userData.phone_no}
                          disabled
                        />
                      </FormGroup>
                      <FormGroup>
                        <CustomTextFieldWithoutControl
                          label="Email"
                          autoComplete="off"
                          variant="outlined"
                          defaultValue={userData.email}
                          disabled
                        />
                      </FormGroup>
                      <div className={classes.spaceDivider}></div>
                      <div className={classes.heading}>
                        <Typography variant="h5" component="h5">
                          Your startup details
                        </Typography>
                      </div>
                      <FormGroup>
                        <CustomTextField
                          name="company_name"
                          control={control}
                          label="Company Name"
                          autoComplete="off"
                          variant="outlined"
                          fullWidth
                          defaultValue={draft.company_name || ''}
                        />
                        {/* <CustomTextField
                          value={draft.company_name || ''}
                          defaultValue={draft.company_name || ''}
                          fullWidth
                          name="company_name"
                          type="text"
                          variant="outlined"
                          style={{ textTransform: 'capitalize' }}
                          label="Company Name"
                          autoComplete="false"
                          control={control}
                          rules={{
                            required: !saveAsADraft
                              ? 'Company name is required'
                              : false,
                            pattern: {
                              value:
                                /^[.@&]?[a-zA-Z0-9 ]+[ !.@&()]?[ a-zA-Z0-9!()]+/,
                              message: 'Invalid Company Name',
                            },
                          }}
                          className={errors.company_name && classes.error}
                        />
                        {errors.company_name && (
                          <Box className={classes.error} color="error.main">
                            <InfoOutlinedIcon />
                            {errors.company_name.message}
                          </Box>
                        )} */}
                      </FormGroup>
                      <FormGroup>
                        <CustomTextField
                          name="product_name"
                          control={control}
                          label="Product Name"
                          autoComplete="off"
                          variant="outlined"
                          fullWidth
                          defaultValue={draft.product_name || ''}
                        />
                        {/* <CustomTextField
                          value={draft.product_name || ''}
                          defaultValue={draft.product_name || ''}
                          fullWidth
                          name="product_name"
                          type="text"
                          variant="outlined"
                          label="Product Name"
                          autoComplete="false"
                          control={control}
                          rules={{
                            required: !saveAsADraft
                              ? 'Product name is required'
                              : false,
                            pattern: {
                              value:
                                /^[.@&]?[a-zA-Z0-9 ]+[ !.@&()]?[ a-zA-Z0-9!()]+/,
                              message: 'Invalid Product Name',
                            },
                          }}
                          className={errors.product_name && classes.error}
                        />
                        {errors.product_name && (
                          <Box className={classes.error} color="error.main">
                            <InfoOutlinedIcon />
                            {errors.product_name.message}
                          </Box>
                        )} */}
                      </FormGroup>
                      <FormGroup>
                        <CustomTextField
                          name="linkedIn_url"
                          control={control}
                          label="LinkedIn URL"
                          autoComplete="off"
                          variant="outlined"
                          fullWidth
                          defaultValue={draft.linkedIn_url || ''}
                        />
                        {/* <CustomTextField
                          defaultValue={draft.linkedIn_url}
                          fullWidth
                          name="linkedIn_url"
                          type="text"
                          label="LinkedIn URL"
                          variant="outlined"
                          autoComplete="false"
                          control={control}
                          rules={{
                            required: !saveAsADraft ? 'URL is required' : false,
                            pattern: {
                              value:
                                /^((https?:\/\/)?((www|\w\w)\.)?linkedin\.com\/)((([\w]{2,3})?)|([^\/]+\/(([\w|\d-&#?=])+\/?){1,}))$/,
                              message: 'Invalid linkedin url',
                            },
                          }}
                          className={errors.linkedIn_url && classes.error}
                        />
                        {errors.linkedIn_url && (
                          <Box className={classes.error} color="error.main">
                            <InfoOutlinedIcon />
                            {errors.linkedIn_url.message}
                          </Box>
                        )} */}
                      </FormGroup>
                      <FormGroup>
                        <Controller
                          control={control}
                          name="stage"
                          defaultValue={draft.stage ? draft.stage : undefined}
                          rules={{
                            required: !saveAsADraft
                              ? 'Stage is required'
                              : false,
                          }}
                          render={({ field }) => (
                            <FormControl
                              variant="outlined"
                              sx={{
                                '& div': {
                                  backgroundColor: 'transparent',
                                  '&:focus': {
                                    backgroundColor: 'transparent',
                                  },
                                },
                              }}
                            >
                              <InputLabel id="stage">Stage</InputLabel>
                              <Select
                                {...field}
                                displayEmpty
                                id="stage"
                                label="Stage"
                                input={<BootstrapInput />}
                              >
                                <MenuItem disabled value="">
                                  Stage
                                </MenuItem>
                                <MenuItem value="Pre-seed">Pre-seed</MenuItem>
                                <MenuItem value="Seed">Seed</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                          // className={errors.stage && classes.error}
                        />
                        {errors.stage && (
                          <Box className={classes.error} color="error.main">
                            <InfoOutlinedIcon />
                            {errors.stage.message}
                          </Box>
                        )}
                      </FormGroup>
                      <FormGroup sx={{ mt: '35px' }}>
                        <CustomTextField
                          name="website_url"
                          control={control}
                          label="Website"
                          autoComplete="off"
                          variant="outlined"
                          fullWidth
                          defaultValue={draft.website_url || ''}
                        />
                        {/* <CustomTextField
                          defaultValue={draft.website_url}
                          fullWidth
                          name="website_url"
                          type="text"
                          variant="outlined"
                          label="Website"
                          autoComplete="false"
                          control={control}
                          rules={{
                            required: !saveAsADraft
                              ? 'Website url is required'
                              : false,
                            pattern: {
                              value:
                                /^((https?|ftp|smtp):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/i,
                              message: 'Invalid Website',
                            },
                          }}
                          className={errors.website_url && classes.error}
                        />
                        {errors.website_url && (
                          <Box className={classes.error} color="error.main">
                            <InfoOutlinedIcon />
                            {errors.website_url.message}
                          </Box>
                        )} */}
                      </FormGroup>
                      <FormGroup>
                        <Controller
                          control={control}
                          name="industry"
                          defaultValue={
                            draft.industry ? draft.industry : undefined
                          }
                          render={({ field }) => (
                            <FormControl
                              variant="outlined"
                              sx={{
                                '& div': {
                                  backgroundColor: 'transparent',
                                  '&:focus': {
                                    backgroundColor: 'transparent',
                                  },
                                },
                              }}
                            >
                              <InputLabel id="stage">Industry</InputLabel>
                              <Select
                                {...field}
                                displayEmpty
                                id="industry"
                                label="Industry"
                                input={<BootstrapInput />}
                              >
                                {industriesData &&
                                  industriesData.map(
                                    (item: any, index: number) => (
                                      <MenuItem value={item.name} key={index}>
                                        {item.name}
                                      </MenuItem>
                                    ),
                                  )}
                                <MenuItem value="Other">Other</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                          // className={errors.stage && classes.error}
                        />
                        {errors.industry && (
                          <Box className={classes.error} color="error.main">
                            <InfoOutlinedIcon />
                            {/* {errors.industry.message} */}
                            {errors.industry?.message}
                          </Box>
                        )}
                        {/* <Controller
                          control={control}
                          variant="outlined"
                          name="industry"
                          defaultValue={draft.industry ? draft.industry : null}
                          rules={{
                            required: !saveAsADraft
                              ? 'Industry is required'
                              : false,
                          }}
                          render={({ onChange, value, onBlur }) => (
                            <FormControl variant="outlined">
                              <InputLabel id="industry">Industry</InputLabel>
                              <Select
                                defaultValue={value}
                                onBlur={(e) => onBlur(e)}
                                id="industry"
                                value={value}
                                displayEmpty
                                label="industry"
                                input={<BootstrapInput />}
                                onChange={(e) => {
                                  onChange(e.target.value);
                                }}
                              >
                                {industriesData &&
                                  industriesData.map((item, index) => (
                                    <MenuItem value={item.name} key={index}>
                                      {item.name}
                                    </MenuItem>
                                  ))}
                                <MenuItem value="Other">Other</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        />
                        {errors.industry && (
                          <Box className={classes.error} color="error.main">
                            <InfoOutlinedIcon />
                            {errors.industry.message}
                          </Box>
                        )} */}
                      </FormGroup>
                      <FormGroup sx={{ mt: '35px' }}>
                        <CustomTextField
                          name="CIN_number"
                          control={control}
                          label="CIN number"
                          autoComplete="off"
                          variant="outlined"
                          fullWidth
                          defaultValue={draft.CIN_number || ''}
                        />
                        {/* <CustomTextField
                          defaultValue={draft.CIN_number}
                          fullWidth
                          name="CIN_number"
                          type="text"
                          variant="outlined"
                          label="CIN number"
                          autoComplete="false"
                          control={control}
                          rules={{
                            required: !saveAsADraft
                              ? 'CIN number is required'
                              : false,
                            pattern: {
                              value:
                                /^([L|U]{1})([0-9]{5})([A-Za-z]{2})([0-9]{4})([A-Za-z]{3})([0-9]{6})$/,
                              message: 'Invalid CIN number',
                            },
                          }}
                          className={errors.CIN_number && classes.error}
                        />
                        {errors.CIN_number && (
                          <Box className={classes.error} color="error.main">
                            <InfoOutlinedIcon />
                            {errors.CIN_number.message}
                          </Box>
                        )} */}
                      </FormGroup>
                      <FormGroup>
                        <CustomTextField
                          name="company_pan_number"
                          control={control}
                          label="Company PAN number"
                          autoComplete="off"
                          variant="outlined"
                          fullWidth
                          defaultValue={draft.company_pan_number || ''}
                        />
                        {/* <CustomTextField
                          defaultValue={draft.company_pan_number}
                          fullWidth
                          name="company_pan_number"
                          type="text"
                          variant="outlined"
                          label="Company PAN Number"
                          autoComplete="false"
                          control={control}
                          rules={{
                            required: !saveAsADraft
                              ? 'Pan number is required'
                              : false,
                            pattern: {
                              value: /^([a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}?$/i,
                              message: 'Invalid pan card number',
                            },
                          }}
                          className={errors.company_pan_number && classes.error}
                        />
                        {errors.company_pan_number && (
                          <Box className={classes.error} color="error.main">
                            <InfoOutlinedIcon />
                            {errors.company_pan_number.message}
                          </Box>
                        )} */}
                      </FormGroup>
                      <FormGroup>
                        <Grid container alignItems="start">
                          <Grid item xs={12} md={3}>
                            <FormLabel>Type of Funding</FormLabel>
                          </Grid>
                          <Grid item xs={12} md={9}>
                            <FormControl
                              // component="fieldset"
                              // item
                              // md={6}
                              className={classes.formControl}
                            >
                              <RadioGroup
                                row
                                // defaultValue={userData.company?.type_of_funding}

                                name="type_of_funding"
                                value={fundingType || '1'}
                                onChange={(e) => setFundingType(e.target.value)}
                                className={classes.RadioGroup}
                              >
                                <FormControlLabel
                                  value={'1'}
                                  control={<Radio color="primary" />}
                                  label="Equity"
                                />

                                <div className={classes.safeLabel}>
                                  <FormControlLabel
                                    value={'2'}
                                    control={<Radio color="primary" />}
                                    label="SAFE"
                                  />
                                </div>
                              </RadioGroup>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </FormGroup>
                      <FormGroup className={classes.FundingField}>
                        <CustomTextFieldAmountNoDecimal
                          control={control}
                          placeholder=""
                          currency="inr"
                          name="funding_goal"
                          variant="outlined"
                          sx={{
                            '& input': {
                              pl: '24px !important',
                            },
                          }}
                          // name="funding_goal"
                          // defaultValue={
                          //   valueWithComma(draft.funding_goal) || ''
                          // }

                          // InputProps={{
                          //   endAdornment: (
                          //     <InputAdornment position="start">
                          //       <h5>₹</h5>
                          //     </InputAdornment>
                          //   ),
                          // }}
                          // inputProps={{
                          //   endAdornment: (
                          //     <InputAdornment position="start">
                          //       <h5>₹</h5>
                          //     </InputAdornment>
                          //   ),
                          // }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Typography
                                  component="span"
                                  fontSize={30}
                                  ml={0.7}
                                >
                                  ₹
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                        />
                        {/* <Controller
                          name="funding_goal"
                          control={control}
                          defaultValue={
                            valueWithComma(draft.funding_goal) || ''
                          }
                          rules={{
                            required: !saveAsADraft
                              ? 'Goal is required'
                              : false,
                            validate: (value) =>
                              value
                                ? parseInt(
                                    value?.toString()?.replaceAll(',', ''),
                                  ) >= 2000000
                                  ? true
                                  : 'Minimum goal amount is 20 Lakh'
                                : true,
                          }}
                          render={({ value, onChange, onBlur }) => {
                            return (
                              <CustomTextField
                                fullWidth
                                name="funding_goal"
                                onBlur={(e) => onBlur(e)}
                                value={value}
                                type="text"
                                // onBlur={e => onBlur(e)}
                                variant="outlined"
                                label="Funding goal"
                                autoComplete="false"
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="start">
                                      <h5>₹</h5>
                                    </InputAdornment>
                                  ),
                                }}
                                onChange={(e) => {
                                  if (
                                    /^[0-9\,b]+$/.test(e.target.value) ||
                                    e.target.value === ''
                                  ) {
                                    onChangeAllNumberValue(e, onChange);
                                  }
                                }}
                                className={
                                  errors.funding_goal && classes.errorWrapper
                                }
                              />
                            );
                          }}
                        />
                        {errors.funding_goal && (
                          <Box className={classes.error} color="error.main">
                            <InfoOutlinedIcon />
                            {errors.funding_goal.message}
                          </Box>
                        )} */}
                      </FormGroup>
                      <FormGroup>
                        <Grid container alignItems="center">
                          <Grid item xs={12} md={4}>
                            <FormLabel>Startup funded before?</FormLabel>
                          </Grid>
                          <Grid item xs={12} md={8}>
                            <FormControl>
                              <RadioGroup
                                row
                                name="funding_received_earlier"
                                value={fundingReceivedEarlier}
                                onChange={(e) =>
                                  setFundingReceivedEarlier(
                                    e.target.value as 'true' | 'false',
                                  )
                                }
                                className={classes.RadioGroup}
                              >
                                <FormControlLabel
                                  value={'true'}
                                  control={<Radio color="primary" />}
                                  label="Yes"
                                />
                                <FormControlLabel
                                  value={'false'}
                                  control={<Radio color="primary" />}
                                  label="No"
                                />
                              </RadioGroup>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </FormGroup>
                      {fundingReceivedEarlier === 'true' && (
                        <>
                          <div className={classes.spaceDivider}></div>
                          <div className={classes.heading}>
                            <Typography variant="h5" component="h5">
                              Your investor details
                            </Typography>
                          </div>
                          {investors &&
                            investors.map((item: any, index: number) => {
                              // console.log(item);
                              return (
                                <div key={index}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                    }}
                                  >
                                    <div className={classes.subtitle}>
                                      Investor {index + 1}
                                    </div>
                                    {investors.length > 1 && (
                                      <div
                                        style={{
                                          marginLeft: 'auto',
                                          cursor: 'pointer',
                                        }}
                                      >
                                        <CloseOutlinedIcon
                                          onClick={() => {
                                            deleteInvestor(index);
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                  <FormGroup>
                                    <CustomTextField
                                      name={`name`}
                                      control={control}
                                      label="Investor Name"
                                      autoComplete="off"
                                      variant="outlined"
                                      fullWidth
                                      defaultValue={item.name || ''}
                                      onChange={(e) =>
                                        investorOnChangeHandler(e, index)
                                      }
                                      rules={{
                                        required: true,
                                      }}
                                    />
                                    {/* <CustomTextField
                                      value={item.name}
                                      fullWidth
                                      name="name"
                                      type="text"
                                      label="Investor Name"
                                      variant="outlined"
                                      onChange={(e) =>
                                        investorOnChangeHandler(e, index)
                                      }
                                      autoComplete="false"
                                      rules={{
                                        required:
                                          fundingReceivedEarlier === 'true'
                                            ? 'Name of invester is required'
                                            : false,
                                      }}
                                      className={
                                        errors.name &&
                                        fundingReceivedEarlier === 'true' &&
                                        classes.error
                                      }
                                    /> */}
                                  </FormGroup>
                                  <FormGroup>
                                    {/* <CustomTextField
                                      variant="outlined"
                                      control={control}
                                      name={`amount${index}`}
                                      placeholder=""
                                      fullWidth
                                      // inputProps={{ style: { textAlign } }}
                                      autoComplete='off'
                                      onPaste={(e) => {
                                        e.preventDefault();
                                      }}
                                      // sx={sx}
                                      transform={{
                                        input: (value: string) => {
                                          if (!value) {
                                            return '';
                                          }
                                          return value;
                                        },
                                        output: (e) => {
                                          const value = e.target.value;
                                          const isValid = /^[0-9,]*$/.test(
                                            value,
                                          );
                                          if (!isValid) {
                                            return value.slice(
                                              0,
                                              value.length - 1,
                                            );
                                          }
                                          const floatVal = parseFloat(
                                            e.target.value.replace(/,/g, ''),
                                          );
                                          const output = getUptoTwo(floatVal);
                                          // if (value[value.length - 1] === '.') {
                                          //   return (
                                          //     output.toLocaleString(currency === 'inr' ? 'en-IN' : 'en-US') +
                                          //     '.'
                                          //   );
                                          // }
                                          return output.toLocaleString(
                                            currency === 'inr'
                                              ? 'en-IN'
                                              : 'en-US',
                                          );
                                        },
                                      }}
                                    /> */}

                                    <Controller
                                      name="amount"
                                      control={control}
                                      defaultValue={
                                        valueWithComma(item.amount) || ''
                                      }
                                      render={({ field }) => {
                                        return (
                                          <Box
                                            sx={{
                                              '& input': {
                                                backgroundColor:
                                                  'transparent !important',
                                                '&:focus': {
                                                  backgroundColor:
                                                    'transparent',
                                                },
                                              },
                                              '& label': {
                                                color:
                                                  'rgba(0,0,0,0.45) !important',
                                              },
                                            }}
                                          >
                                            <CustomTextFieldOld
                                              fullWidth
                                              name="amount"
                                              value={valueWithComma(
                                                item.amount,
                                              )}
                                              type="text"
                                              variant="outlined"
                                              label="Investment Amount"
                                              autoComplete="false"
                                              onChange={(e: any) => {
                                                if (
                                                  /^[0-9\,b]+$/.test(
                                                    e.target.value,
                                                  ) ||
                                                  e.target.value === ''
                                                ) {
                                                  investorOnChangeHandler(
                                                    e,
                                                    index,
                                                    field.onChange,
                                                  );
                                                }
                                              }}
                                              className={
                                                errors.amount && classes.error
                                              }
                                            />
                                          </Box>
                                        );
                                      }}
                                    />
                                    {fundingReceivedEarlier === 'true' && (
                                      <Box
                                        className={classes.error}
                                        color="error.main"
                                      >
                                        {isEmptyInvestor &&
                                        isEmptyInvestor.includes(index) ? (
                                          <>
                                            <InfoOutlinedIcon />
                                            {/* Please enter the investment amount */}
                                            Please fill investor details
                                          </>
                                        ) : invalidAmount &&
                                          invalidAmount.includes(index) ? (
                                          <>
                                            <InfoOutlinedIcon />
                                            {/* Please enter valid amount */}
                                            Please fill investor details
                                          </>
                                        ) : null}
                                      </Box>
                                    )}
                                  </FormGroup>
                                  <hr className={classes.hr} />
                                </div>
                              );
                            })}
                          <FormGroup>
                            <div
                              className={classes.addInvestor}
                              onClick={() => addAnotherInvestor()}
                            >
                              <AddIcon /> <span>Add another investor</span>
                            </div>
                          </FormGroup>
                        </>
                      )}
                      <FormGroup>
                        <div className={classes.formBtn}>
                          {draftLoader ? (
                            <CustomButton
                              name="draft"
                              fullWidth
                              // bgColor="#85edd7"
                              // fontSize="14"
                              // bordercolor="#85edd7"
                              // fontColor="#000000"
                              // radius="40"
                              // btnPadding="15"
                              variant="contained"
                              sx={{
                                fontSize: 20,
                                height: 50,
                                width: {
                                  xs: '100%',
                                  md: 200,
                                },
                                display: 'block',
                                margin: 'auto',
                              }}
                            >
                              <CircularProgress color="inherit" size={14} />
                            </CustomButton>
                          ) : (
                            <CustomButton
                              type="button"
                              onClick={() => onSubmitWithDraft(onDraft)}
                              name="draft"
                              disabled={draftLoader || loader}
                              fullWidth
                              variant="outlined"
                              sx={{
                                fontSize: 20,
                                height: 50,
                                width: {
                                  xs: '100%',
                                  md: 200,
                                },
                                display: 'block',
                                margin: 'auto',
                              }}
                              // bgColor="transparent"
                              // fontSize="14"
                              // bordercolor="#008B5E"
                              // fontColor="#008B5E"
                              // radius="40"
                              // btnPadding="15"
                            >
                              Save draft
                            </CustomButton>
                          )}

                          {loader ? (
                            <CustomButton
                              variant="contained"
                              sx={{
                                fontSize: 20,
                                height: 50,
                                width: {
                                  xs: '100%',
                                  md: 200,
                                },
                                display: 'block',
                                margin: 'auto',
                              }}
                            >
                              <CircularProgress color="inherit" size={14} />
                            </CustomButton>
                          ) : (
                            <CustomButton
                              type="button"
                              onClick={() =>
                                onSubmitWithLaunch(handleSubmit(onSave))
                              }
                              name="save"
                              disabled={draftLoader || loader}
                              fullWidth
                              variant="contained"
                              sx={{
                                fontSize: 20,
                                height: 50,
                                width: {
                                  xs: '100%',
                                  md: 200,
                                },
                                display: 'block',
                                margin: 'auto',
                              }}
                            >
                              Submit
                            </CustomButton>
                          )}
                        </div>
                      </FormGroup>
                    </form>
                  </div>
                </Grid>
              </Grid>
            </Container>
          </div>
        </CommonLayout>
        {/* <CommonSwitchDashboard /> */}
      </>
    )
  );
}
